import type {GetStaticProps} from 'next'
import {createClient} from 'prismicio'
import type {ButtonDocument, EventDocument, NavigationDocumentData, PageDocument} from 'prismicio-types'
import {PageRouter as Page} from 'src/PageRouter'
import {logger} from 'src/common/logger'
import type {EventType} from 'src/common/utils/eventRoutes'
import {EVENTS_PER_PAGE, EVENT_GRAPH_QUERY, EVENT_TYPES, fetchEventsCommonData} from 'src/common/utils/eventRoutes'

export default Page

export const getStaticProps: GetStaticProps<{
  navigationData: NavigationDocumentData
  buttonsData?: ButtonDocument[]
  page: PageDocument
  eventsByType: Record<string, EventDocument<string>[]>
  eventTypes: EventType[]
  totalPages: number
}> = async ({locale, previewData}) => {
  const client = createClient({previewData})

  try {
    const {navigationData, buttonsData, page} = await fetchEventsCommonData(locale || 'en-us', previewData)

    // Fetch all events without filtering - Prismic doesn't support filtering on event_type field
    const allEventsQuery = await client.getByType<EventDocument<string>>('event', {
      lang: locale,
      graphQuery: EVENT_GRAPH_QUERY,
      orderings: {field: 'my.event.event_date', direction: 'asc'},
    })

    // Filter events in memory by type
    const onDemandEvents = allEventsQuery.results
      .filter((event) => event.data.event_type === EVENT_TYPES.ON_DEMAND)
      .reverse()

    const inPersonEvents = allEventsQuery.results.filter((event) => event.data.event_type === EVENT_TYPES.IN_PERSON)

    const onlineEvents = allEventsQuery.results.filter((event) => event.data.event_type === EVENT_TYPES.ONLINE)

    // Paginate "On demand" events
    const onDemandPage1 = onDemandEvents.slice(0, EVENTS_PER_PAGE)
    const totalPages = Math.ceil(onDemandEvents.length / EVENTS_PER_PAGE)

    const eventsByType: Record<string, EventDocument<string>[]> = {
      [EVENT_TYPES.ON_DEMAND]: onDemandPage1,
      [EVENT_TYPES.IN_PERSON]: inPersonEvents,
      [EVENT_TYPES.ONLINE]: onlineEvents,
    }

    const eventTypes = [EVENT_TYPES.IN_PERSON, EVENT_TYPES.ONLINE, EVENT_TYPES.ON_DEMAND].filter(
      (type) => eventsByType[type]?.length > 0,
    )

    return {
      props: {
        navigationData,
        ...(buttonsData.length > 0 ? {buttonsData} : {}),
        page,
        eventsByType,
        eventTypes,
        totalPages,
      },
      revalidate: Number(process.env.REVALIDATE_TIME) || 300,
    }
  } catch (error) {
    logger.error('Error fetching events-hub page data', {route: 'events-hub', locale}, error)
    return {notFound: true}
  }
}
